import type { RoundOdds } from 'src/types/domain/RoundOdds'
import { RTP_PPM } from 'src/lib/fairness/birdie'
import { effectiveRtp, hasLightning } from 'src/utils/effectiveRtp'

type T = (key: string, options?: Record<string, unknown>) => string

/** The pricing key Birdie is filed under in `effectiveRtp` — by game, not by race-configuration id,
 *  so a new environment can never silently drop the effective figure. */
export const BIRDIE_GAME_KEY = 'birdie_time'

/**
 * Birdie's board changes every round, so the template-market scan in
 * getEffectiveBetStats would quote a number no player can see. The base RTP
 * is constant (every option is single-selection at the same margin) and comes
 * from the round's own `rtp_ppm`; the effective figure is the same
 * lightning-and-level lift every other game quotes (`effectiveRtp`, one pool of
 * twelve); the max payout is this round's largest multiplier, labelled as
 * varying (A56). The catalogue-wide ceiling lives in the 155-docs payout table.
 */
export function birdieRtpRow(
  odds: RoundOdds | undefined,
  t: T
): { maxPayout: string; rtp: string; effective?: string } {
  const base = (odds?.rtpPpm ?? RTP_PPM) / 10_000
  const rtp = `${base.toFixed(2)}%`
  const lifted = effectiveRtp(BIRDIE_GAME_KEY, base, '', 1)
  const effective =
    hasLightning(BIRDIE_GAME_KEY) && lifted !== null && lifted > base
      ? `${lifted.toFixed(2)}%`
      : undefined
  if (!odds) return { maxPayout: t('varies every round'), rtp, effective }
  const max = Math.max(...Object.values(odds.multipliers))
  return {
    maxPayout: t('up to {{max}}x this round — varies every round', { max: max.toFixed(2) }),
    rtp,
    effective
  }
}
